import React from 'react';
import { Text, View, Pressable, StyleSheet, Image } from 'react-native';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { __WEB__, COLOR, FONT, FONT_SIZE, THEME } from '../../constants/styles';
import { addCase, updateCase, resetStatusState, resetState, cancelReadiness } from './actions';
import { resetOktaTokenForUser } from '../auth/okta/slice';
import { resetMFATokenForEmployerUser } from '../auth/actions';

import {
  getEditingCase,
  getEditingCaseEmployer,
  getEditingCaseEmployerType,
  getEditingCasePatient,
  getEditingCasePatientProfile,
  getEditingCasePatientEmployerUser,
} from './selector';
import { getCurrentEmployer } from '../../selectors/entities';

import {
  PATIENT_INFO,
  RESIDENT_ADMINISTRATION,
  READINESS_CLEARANCE,
} from '../../constants/permissions';
import { editingCasePermissions, currentEmployerPermissions } from '../../helpers/permissions';

import {
  OPComponent,
  Container,
  NavBar,
  I18n,
  Navigation,
  Utils,
  ProfileAvatar,
} from '../components';

import { EMPLOYER_TYPE_AIRFORCE, EMPLOYER_TYPE_MED_RECRUITER } from '../../constants/employerTypes';

import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../actions/statusTypes';

import AddEditAirforce from './airforce/add_edit';
import AddEditMedRecruiter from './medRecruiter/add_edit';
import CaseFields from './case_fields_employer';

class CaseForm extends OPComponent {
  static navigatorButtons = NavBar && {
    leftButtons: [NavBar.buttons.cancel],
  };

  constructor(props) {
    super(props);

    this.state = {};

    this._isUpdate = !!props.editingCase;
    this._isCreate = !this._isUpdate;
    this._isEditable = !this._isUpdate || props.canEditEditingCase;
    this._caseFormRef = React.createRef();
  }

  navigationButtonPressed = ({ buttonId }) => {
    if (this.props.status === REQUESTING) return;

    if (NavBar.handleButtonPress2(buttonId, this)) return;
  };

  componentDidMount() {
    Navigation.registerForButtonEvents(this);
    Utils.trackScreen('Cases', 'Case Form');
    Navigation.setButtons(
      {
        leftButtons: [NavBar.buttons.cancel],
        rightButtons: this._isEditable ? [NavBar.buttons.save] : [],
      },
      this.props.componentId
    );

    this.props.actions.resetStatusState();
  }

  componentWillUnmount() {
    Navigation.unRegisterForButtonEvents(this);
    // if we got here via case info screen (isUpdate is true)
    // then dont clear the screen state except for request status.
    // cases screen complete state will be cleared when we exit case info
    // if its a new case then we didn't get here via case info so
    // we need to clear all of the cases screen state
    if (this._isUpdate) {
      this.props.actions.resetStatusState();
    } else {
      this.props.actions.resetState();
    }
    this.props.onCloseScreen && this.props.onCloseScreen(this._isUpdate);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.mfaResetStatus === SUCCESS && prevProps.mfaResetStatus !== SUCCESS) {
      this.setState({ successMessage: 'Reset!' });
    }
    if (this.props.resetOktaTokenStatus === SUCCESS && prevProps.resetOktaTokenStatus !== SUCCESS) {
      this.setState({ successMessage: 'Reset!' });
    }
  }

  // called when Save navbar button is tapped
  onSave = () => {
    this.submit();
  };

  errorMessageFromProps() {
    return (
      this.props.errorMessage?.message ||
      this.props.mfaErrorMessage?.message ||
      this.props.resetOktaTokenError?.message ||
      null
    );
  }

  handleCaseSave = () => {
    const props = this.props;

    if (this._isCreate) {
      this.goToCaseInfo();
    } else {
      this.setState({ successMessage: 'Saved!' });

      Navigation.setButtons(
        {
          leftButtons: [NavBar.buttons.done],
          rightButtons: this._isEditable ? [NavBar.buttons.save] : [],
        },
        props.componentId
      );
    }
  };

  submit = () => {
    Utils.trackEvent('Action', 'Tap', 'Submit Case');

    const formIsValid = this._caseFormRef.current?.submit();

    if (!formIsValid) {
      this.setState({ errorMessage: I18n.t('form.pleaseResolveErrors') });
      return;
    }
  };

  goToCaseInfo = () => {
    Navigation.push(
      {
        screen: 'CaseInfo',
        passProps: {
          noBacksies: true,
          caseCreateSuccessMessage: I18n.aec('airforce.airmanAddSuccess'),
        },
      },
      this.props.componentId
    );
  };

  handleConfirmationOpts = (confirmationOpts) => {
    this.setState({
      confirmationOpts: {
        ...confirmationOpts,
        onConfirmed: () => {
          confirmationOpts.onConfirmed?.();
          this._closeConfirmation();
        },
        onCancelled: () => {
          confirmationOpts.onCancelled?.();
          this._closeConfirmation();
        },
      },
    });
  };

  resetMFAToken = () => {
    const userId = this.props.editingCasePatient?.userId;
    const employerId = this.props.editingCase?.employerId;
    if (!userId || !employerId) return;

    this.props.actions.resetMFATokenForEmployerUser(userId, employerId);
  };

  resetOktaToken = () => {
    const userId = this.props.editingCasePatient?.userId;
    const employerId = this.props.editingCase?.employerId;
    if (!userId || !employerId) return;

    this.props.actions.resetOktaTokenForUser({ userId, employerId });
  };
  confirmResetMFAToken = () => {
    this.setState({
      confirmationOpts: {
        title: 'Reset the MFA Token?',
        description: `Reset the MFA token so the user can set a new one next time they log in`,
        confirm: 'Yes, Reset',
        onConfirmed: () => {
          this.resetMFAToken();
          this._closeConfirmation();
        },
        onCancelled: this._closeConfirmation,
      },
    });
  };
  _closeConfirmation = () => {
    this.setState({ confirmationOpts: null });
  };

  confirmResetOktaToken = () => {
    this.setState({
      confirmationOpts: {
        title: 'Reset the Okta Token?',
        description: `Reset the Okta token so the user can set a new one next time they log in`,
        confirm: 'Yes, Reset',
        onConfirmed: () => {
          this.resetOktaToken();
          this._closeConfirmation();
        },
        onCancelled: this._closeConfirmation,
      },
    });
  };

  handleChangeText(stateAttrName, text) {
    this.setState({
      [stateAttrName]: text,
    });
  }

  handleImageSelected = (profileImage) => {
    Utils.trackEvent('Action', 'Tap', 'Image Added to Message');

    this.setState({ profileImage });
  };

  renderAvatarHeader() {
    const props = this.props;
    if (!props.editingCase) return null;
    return (
      <View
        style={[
          styles.header,
          THEME.basicRow,
          { justifyContent: 'flex-start', alignItems: 'center', paddingBottom: 10 },
        ]}
      >
        <ProfileAvatar
          initialsStr={props.editingCase.name}
          profile={props.patientProfile}
          circleStyles={{ width: 50, height: 50 }}
        />
        <View style={{ paddingBottom: 10 }}>
          <Text style={styles.caseNameText}>{props.editingCase?.name}</Text>
          <Text style={styles.roleText}>{props.employerUser?.role}</Text>
        </View>
      </View>
    );
  }

  render() {
    const props = this.props;

    const isUpdate = this._isUpdate;

    if (isUpdate && !props.canViewEditingCase) return null;
    if (!isUpdate && !props.canCreateResident) return null;

    const formEmployerType = props.editingCase
      ? props.editingCaseEmployerType
      : props.currentEmployerType || null;

    const CaseFieldsComponent =
      formEmployerType === EMPLOYER_TYPE_AIRFORCE
        ? AddEditAirforce
        : formEmployerType === EMPLOYER_TYPE_MED_RECRUITER
          ? AddEditMedRecruiter
          : CaseFields;

    return (
      <Container
        keyboardAvoidingScrollView={true}
        renderActivityOverlay={
          this.props.status === REQUESTING || this.props.mfaResetStatus === REQUESTING
        }
        successMessage={this.state.successMessage}
        errorMessage={
          !this.state.successMessage && (this.state.errorMessage || this.errorMessageFromProps())
        }
        confirmationOpts={this.state.confirmationOpts}
        contentContainerStyle={__WEB__ ? { paddingHorizontal: 25 } : {}}
      >
        {this.renderAvatarHeader()}

        <CaseFieldsComponent
          ref={this._caseFormRef}
          editable={this._isEditable}
          editingCase={this.props.editingCase}
          editingCasePatient={this.props.editingCasePatient}
          editingCaseEmployer={props.editingCaseEmployer}
          componentId={this.props.componentId}
          updateCase={this.props.actions.updateCase}
          addCase={this.props.actions.addCase}
          caseStatus={this.props.status}
          currentEmployer={this.props.currentEmployer}
          onSave={this.handleCaseSave}
          onConfirmationOpts={this.handleConfirmationOpts}
        />

        {!!isUpdate && props.isSuper && props.isOkta && (
          <Pressable style={styles.footerButton} onPress={this.confirmResetOktaToken}>
            <Image
              style={[styles.footerIcon, styles.mfaFooterIcon]}
              source={require('../../../resources/icon-repeat.png')}
              resizeMode="contain"
            />
            <Text style={[styles.footerButtonText, styles.mfaFooterButtonText]}>
              RESET OKTA TOKEN
            </Text>
          </Pressable>
        )}

        {!!isUpdate && props.isSuper && !props.isOkta && (
          <Pressable style={styles.footerButton} onPress={this.confirmResetMFAToken}>
            <Image
              style={[styles.footerIcon, styles.mfaFooterIcon]}
              source={require('../../../resources/icon-repeat.png')}
              resizeMode="contain"
            />
            <Text style={[styles.footerButtonText, styles.mfaFooterButtonText]}>
              RESET MFA TOKEN
            </Text>
          </Pressable>
        )}

        <View style={{ paddingBottom: 50 }} />
      </Container>
    );
  }
}

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: 25,
    paddingTop: 20,
    backgroundColor: COLOR.contrast,
    alignItems: 'center',
  },
  inputsContainer: THEME.inputsContainer,
  footerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 35,
    paddingVertical: 20,
  },
  footerIcon: {
    tintColor: COLOR.emphasis,
    width: 13,
    height: 15,
    marginRight: 20,
  },
  footerButtonText: {
    ...FONT.semibold,
    fontSize: FONT_SIZE.small,
    color: COLOR.emphasis,
  },
  mfaFooterIcon: {
    tintColor: COLOR.textLight,
  },
  mfaFooterButtonText: {
    color: COLOR.textLight,
  },
  caseNameText: {
    ...FONT.medium,
    fontSize: FONT_SIZE.xlarge,
    color: COLOR.textLight,
    marginLeft: 15,
  },
  roleText: {
    ...FONT.regular,
    fontSize: FONT_SIZE.small,
    color: COLOR.textLight,
    marginLeft: 15,
    marginTop: 3,
  },
});

function mapStateToProps(state, ownProps) {
  const editingCaseEmployer = getEditingCaseEmployer(state);
  const currentEmployer = getCurrentEmployer(state);
  const isSuper = editingCaseEmployer
    ? editingCaseEmployer.isSuperadmin
    : currentEmployer?.isSuperadmin;
  return {
    status: state.screens.cases.status,
    errorMessage: state.screens.cases.errorMessage,
    editingCase: getEditingCase(state),
    editingCasePatient: getEditingCasePatient(state),
    editingCaseId: state.screens.cases.caseInfoCaseId,
    canEditEditingCase: editingCasePermissions(RESIDENT_ADMINISTRATION).canEdit,
    canViewEditingCase: editingCasePermissions(RESIDENT_ADMINISTRATION).canView,
    canCreateResident: currentEmployerPermissions(RESIDENT_ADMINISTRATION).canEdit,
    editingCaseEmployer,
    editingCaseEmployerType: getEditingCaseEmployerType(state),
    currentEmployer,
    currentEmployerType: currentEmployer?.uiConfig?.type,
    canViewEditingCasePatientInfo: editingCasePermissions(PATIENT_INFO).canView,
    canEditEditingCasePatientInfo: editingCasePermissions(PATIENT_INFO).canEdit,
    canEditReadnessClearance: editingCasePermissions(READINESS_CLEARANCE).canEdit,
    mfaResetStatus: state.screens.cases.mfaResetStatus,
    mfaErrorMessage: state.screens.cases.mfaErrorMessage,
    isSuper: !!isSuper,
    patientProfile: getEditingCasePatientProfile(state),
    employerUser: getEditingCasePatientEmployerUser(state),
    isOkta: !!state.user.oktaAuthToken,
    resetOktaTokenStatus: state.screens.oktaAuth.resetOktaTokenStatus || 'PENDING',
    resetOktaTokenError: null,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        addCase,
        updateCase,
        cancelReadiness,
        resetStatusState,
        resetState,
        resetMFATokenForEmployerUser,
        resetOktaTokenForUser,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CaseForm);
